import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, CheckCircle2, Circle, Radio, Search, ListChecks } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// The four activate-agents the user sees running (studio_integrator is the
// silent critic/gate at the end).
const ROLE_LABELS = {
  marketing_strategist: 'Positionering',
  findability_auditor: 'Vindbaarheidsplan',
  inbound_marketer: 'Inbound & content',
  ops_director: 'Operatie',
  studio_integrator: 'Samenhang & controle',
};

function Step({ state, icon: Icon, label, sub }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5">
        {state === 'done' && <CheckCircle2 className="w-5 h-5 text-green-600" />}
        {state === 'active' && <Loader2 className="w-5 h-5 text-primary animate-spin" />}
        {state === 'pending' && <Circle className="w-5 h-5 text-muted-foreground" />}
      </div>
      <div>
        <p className={`text-sm font-medium ${state === 'pending' ? 'text-muted-foreground' : 'text-foreground'}`}>
          <Icon className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />{label}
        </p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
    </div>
  );
}

// Resume the wizard from whatever state the project/audit is already in, so a
// reload or closed tab mid-flow never leaves the project stuck.
function initialPhase(project, audit) {
  const s = project?.activation_status;
  if (audit?.status === 'complete') return 'ready_to_plan';
  if (audit && (audit.status === 'running' || audit.status === 'pending')) return 'auditing';
  if (s === 'auditing') return 'auditing';
  return 'form'; // draft, or a failed audit → let the user (re)start
}

export default function IntakeWizard({ project, audit, onComplete }) {
  const [phase, setPhase] = useState(() => initialPhase(project, audit)); // form | auditing | ready_to_plan | pipeline | done
  const [domain, setDomain] = useState(project?.domain || '');
  const [description, setDescription] = useState(project?.description || '');
  const [gscProperty, setGscProperty] = useState(project?.gsc_property || '');
  const [auditNote, setAuditNote] = useState(
    audit?.status === 'failed' ? `Vorige scan mislukte: ${audit.error || 'onbekende fout'}. Probeer opnieuw.` : ''
  );
  const [steps, setSteps] = useState({}); // role -> pending|active|done|error
  const [rolesOrder, setRolesOrder] = useState([]);
  const esRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    // If we mounted mid-audit, resume polling for completion.
    if (phase === 'auditing') pollAudit();
    return () => {
      if (esRef.current) esRef.current.close();
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startIntake = async () => {
    if (!domain.trim()) { toast.error('Vul een domein in'); return; }
    setPhase('auditing');
    setAuditNote('Scan gestart — we bekijken robots, sitemap, bot-toegang en je pagina\'s…');
    try {
      await axios.post(`${API}/projects/${project.id}/intake`, {
        domain: domain.trim(),
        description: description.trim() || undefined,
        gsc_property: gscProperty.trim() || undefined,
      });
      pollAudit();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Intake mislukt');
      setPhase('form');
    }
  };

  const pollAudit = () => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/projects/${project.id}/audit`);
        const st = res.data.status;
        if (st === 'complete') {
          clearInterval(pollRef.current);
          setAuditNote('Scan klaar. laibel stelt nu je plan samen…');
          runPipeline();
        } else if (st === 'failed') {
          clearInterval(pollRef.current);
          toast.error('Scan mislukt: ' + (res.data.error || 'onbekend'));
          setPhase('form');
        }
      } catch (e) {
        // 404 = audit doc not visible yet; keep polling
      }
    }, 3000);
  };

  const runPipeline = async () => {
    try {
      const res = await axios.post(`${API}/orchestrations`, {
        project_id: project.id,
        pipeline: 'activate',
        mode: 'auto',
      });
      const toRun = res.data.to_run || [];
      setRolesOrder(toRun);
      const init = {};
      toRun.forEach((r) => (init[r] = 'pending'));
      setSteps(init);
      setPhase('pipeline');
      openStream(res.data.id);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Plan-run starten mislukt');
      setPhase('form');
    }
  };

  const openStream = (orchestrationId) => {
    const url = `${API}/orchestrations/${orchestrationId}/events`;
    const es = new EventSource(url, { withCredentials: true });
    esRef.current = es;
    es.onmessage = (evt) => {
      let data;
      try { data = JSON.parse(evt.data); } catch { return; }
      if (data.type === 'step_start') {
        setSteps((s) => ({ ...s, [data.role]: 'active' }));
      } else if (data.type === 'step_done') {
        setSteps((s) => ({ ...s, [data.role]: 'done' }));
      } else if (data.type === 'step_error') {
        setSteps((s) => ({ ...s, [data.role]: 'error' }));
      } else if (data.type === 'done') {
        es.close();
        if (data.status === 'complete') {
          setPhase('done');
          toast.success('Je plan staat klaar!');
          onComplete && onComplete();
        } else {
          toast.error('De pipeline liep vast — check de logs');
          setPhase('form');
        }
      }
    };
    es.onerror = () => {
      es.close();
      toast.error('Verbinding met de plan-stream verbroken');
    };
  };

  if (phase === 'form') {
    return (
      <Card data-testid="intake-wizard">
        <CardHeader>
          <CardTitle>Activeer je app</CardTitle>
          <CardDescription>
            Geef je domein. laibel scant je site, stelt een plan op en jij keurt goed. Er gaat niets automatisch live.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="domain">Domein *</Label>
            <Input id="domain" placeholder="https://jouwapp.nl" value={domain}
                   onChange={(e) => setDomain(e.target.value)} data-testid="intake-domain-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="desc">Wat doet je app? Voor wie?</Label>
            <Textarea id="desc" rows={3} value={description}
                      placeholder="Bijv. Lesvoorbereiding voor basisschoolleerkrachten — genereer in minuten een complete les."
                      onChange={(e) => setDescription(e.target.value)} data-testid="intake-desc-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gsc">Google Search Console-property (optioneel)</Label>
            <Input id="gsc" placeholder="sc-domain:jouwapp.nl" value={gscProperty}
                   onChange={(e) => setGscProperty(e.target.value)} data-testid="intake-gsc-input" />
          </div>
          <Button onClick={startIntake} className="w-full" data-testid="intake-start-btn">
            <Search className="w-4 h-4 mr-2" /> Scan mijn site
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (phase === 'ready_to_plan') {
    return (
      <Card data-testid="intake-resume">
        <CardHeader>
          <CardTitle>Scan klaar</CardTitle>
          <CardDescription>{domain} — laibel heeft je site in kaart. Zet de laatste stap: stel je plan op.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button onClick={runPipeline} data-testid="intake-run-plan-btn">
            <ListChecks className="w-4 h-4 mr-2" /> Stel plan op
          </Button>
          <Button variant="outline" onClick={() => setPhase('form')}>
            <Search className="w-4 h-4 mr-2" /> Opnieuw scannen
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="intake-progress">
      <CardHeader>
        <CardTitle>{phase === 'done' ? 'Plan klaar' : 'laibel is bezig…'}</CardTitle>
        <CardDescription>{domain}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <Step
          state={phase === 'auditing' ? 'active' : 'done'}
          icon={Radio}
          label="Site scannen"
          sub={phase === 'auditing' ? auditNote : 'Crawlability, bot-toegang en pagina\'s in kaart gebracht'}
        />
        <div className="border-l-2 border-gray-100 ml-2.5 pl-6 space-y-4 py-1">
          {rolesOrder.length === 0 && phase !== 'auditing' && (
            <p className="text-xs text-muted-foreground">Plan wordt voorbereid…</p>
          )}
          {rolesOrder.map((role) => (
            <Step key={role} state={steps[role] === 'error' ? 'active' : (steps[role] || 'pending')}
                  icon={ListChecks} label={ROLE_LABELS[role] || role}
                  sub={steps[role] === 'error' ? 'Er ging iets mis bij deze stap' : null} />
          ))}
        </div>
        {phase === 'done' && (
          <Button onClick={onComplete} className="w-full" data-testid="intake-view-plan-btn">
            <ListChecks className="w-4 h-4 mr-2" /> Bekijk je plan
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
